export type PathCoord = { x: number; y: number };

function sign(n: number): number {
  return n < 0 ? -1 : 1;
}

function monotoneSlope3(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  const h0 = x1 - x0;
  const h1 = x2 - x1;
  const s0 = h0 === 0 ? 0 : (y1 - y0) / h0;
  const s1 = h1 === 0 ? 0 : (y2 - y1) / h1;
  const p = h0 + h1 === 0 ? 0 : (s0 * h1 + s1 * h0) / (h0 + h1);
  return (sign(s0) + sign(s1)) * Math.min(Math.abs(s0), Math.abs(s1), 0.5 * Math.abs(p)) || 0;
}

function monotoneSlope2(t: number, x0: number, y0: number, x1: number, y1: number): number {
  const h = x1 - x0;
  return h ? (3 * (y1 - y0) / h - t) / 2 : t;
}

/** Monotone cubic SVG path through the given coords (visual interpolation only). */
export function monotoneCubicPath(coords: PathCoord[]): string {
  const n = coords.length;
  if (n === 0) return '';
  const start = `M${coords[0].x.toFixed(2)},${coords[0].y.toFixed(2)}`;
  if (n === 1) return start;
  if (n === 2) {
    return `${start} L${coords[1].x.toFixed(2)},${coords[1].y.toFixed(2)}`;
  }

  const tangents = new Array<number>(n);
  for (let i = 1; i < n - 1; i++) {
    tangents[i] = monotoneSlope3(
      coords[i - 1].x,
      coords[i - 1].y,
      coords[i].x,
      coords[i].y,
      coords[i + 1].x,
      coords[i + 1].y
    );
  }
  tangents[0] = monotoneSlope2(tangents[1], coords[0].x, coords[0].y, coords[1].x, coords[1].y);
  tangents[n - 1] = monotoneSlope2(
    tangents[n - 2],
    coords[n - 2].x,
    coords[n - 2].y,
    coords[n - 1].x,
    coords[n - 1].y
  );

  const parts = [start];
  for (let i = 0; i < n - 1; i++) {
    const x0 = coords[i].x;
    const y0 = coords[i].y;
    const x1 = coords[i + 1].x;
    const y1 = coords[i + 1].y;
    const dx = (x1 - x0) / 3;
    parts.push(
      `C${(x0 + dx).toFixed(2)},${(y0 + dx * tangents[i]).toFixed(2)} ${(x1 - dx).toFixed(2)},${(y1 - dx * tangents[i + 1]).toFixed(2)} ${x1.toFixed(2)},${y1.toFixed(2)}`
    );
  }
  return parts.join('');
}

export function buildAreaPath(linePath: string, baselineY: number, firstX: number, lastX: number): string {
  if (!linePath) return '';
  return `${linePath} L${lastX.toFixed(2)},${baselineY.toFixed(2)} L${firstX.toFixed(2)},${baselineY.toFixed(2)} Z`;
}

export function scaleValue(value: number, min: number, max: number, plotHeight: number): number {
  if (max === min) return plotHeight / 2;
  const normalized = (value - min) / (max - min);
  return plotHeight - normalized * plotHeight;
}

export function computeNiceAxisTicks(min: number, max: number, tickCount = 4): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0];
  if (max <= min) {
    const padded = max === 0 ? 1 : max * 1.1;
    return Array.from({ length: tickCount }, (_, index) => (padded / (tickCount - 1)) * index);
  }
  return Array.from({ length: tickCount }, (_, index) => min + ((max - min) / (tickCount - 1)) * index);
}

export function computeAxisMax(values: number[]): number {
  const filtered = values.filter((value) => Number.isFinite(value));
  const max = filtered.length ? Math.max(...filtered) : 0;
  if (max <= 0) return 1;
  return max * 1.05;
}
